import React, { useState, useEffect, useRef, useMemo } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import {
  MdUploadFile,
  MdDelete,
  MdToggleOn,
  MdToggleOff,
  MdRefresh,
  MdCalendarMonth,
  MdCheckCircle,
  MdPending,
} from 'react-icons/md';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_NAMES_TE = ['జన', 'ఫిబ్ర', 'మార్', 'ఏప్ర', 'మే', 'జున్', 'జూలై', 'ఆగస్', 'సెప్', 'అక్టో', 'నవ', 'డిసెం'];

export default function TeluguCalendar() {
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedState, setSelectedState] = useState('Telangana');
  const [filterState, setFilterState] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [detailCalendar, setDetailCalendar] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const fileInputRef = useRef(null);

  // Load full calendar details when clicking on a card
  const loadCalendarDetail = async (calendar) => {
    setLoadingDetail(true);
    try {
      const res = await api.get('/admin/calendars', {
        params: {
          year: calendar.year,
          month: calendar.month,
          state: calendar.state,
        },
      });
      if (res.data?.success) {
        setDetailCalendar(res.data.data);
      } else {
        setDetailCalendar(calendar);
        toast.error('Could not load full calendar details');
      }
    } catch (err) {
      console.error('Error loading detail:', err);
      setDetailCalendar(calendar);
      toast.error('Failed to load calendar details');
    } finally {
      setLoadingDetail(false);
    }
  };

  // Load calendars
  const loadCalendars = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterState) params.state = filterState;
      if (filterYear) params.year = filterYear;

      const res = await api.get('/admin/calendars/list', { params });
      if (res.data?.success) {
        setCalendars(res.data.data || []);
      }
    } catch (err) {
      console.error('Error loading calendars:', err);
      toast.error('Failed to load calendars');
    } finally {
      setLoading(false);
    }
  };

  // Calculate stats
  const stats = useMemo(() => {
    const total = calendars.length;
    const active = calendars.filter(c => c.is_active).length;
    const byYear = new Map();
    calendars.forEach(c => {
      if (!byYear.has(c.year)) byYear.set(c.year, []);
      byYear.get(c.year).push(c);
    });
    return { total, active, byYear, years: Array.from(byYear.keys()).sort((a, b) => b - a) };
  }, [calendars]);

  // Filter calendar list
  const filteredCalendars = useMemo(() => {
    let filtered = calendars;
    if (filterState) filtered = filtered.filter(c => c.state === filterState);
    if (filterYear) filtered = filtered.filter(c => c.year === parseInt(filterYear));
    if (filterMonth) filtered = filtered.filter(c => c.month === parseInt(filterMonth));
    return filtered.sort((a, b) => b.year - a.year || b.month - a.month);
  }, [calendars, filterState, filterYear, filterMonth]);

  // Get month coverage for current filter
  const monthCoverage = useMemo(() => {
    const coverage = new Array(12).fill(false);
    calendars
      .filter(c => !filterState || c.state === filterState)
      .forEach(c => {
        coverage[c.month - 1] = true;
      });
    return coverage;
  }, [calendars, filterState]);

  useEffect(() => {
    loadCalendars();
  }, []);

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.json')) {
        toast.error('Please select a JSON file');
        return;
      }
      setSelectedFile(file);
    }
  };

  // Handle file upload
  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a JSON file');
      return;
    }

    setUploading(true);
    try {
      // Read file content
      const fileContent = await selectedFile.text();
      const calendarData = JSON.parse(fileContent);

      // Upload
      const res = await api.post('/admin/calendars/upload', {
        state: selectedState,
        calendar_data: calendarData,
      });

      if (res.data?.success) {
        toast.success('Calendar imported successfully!');
        setSelectedFile(null);
        setSelectedState('Telangana');
        fileInputRef.current.value = '';
        loadCalendars();
      } else {
        toast.error(res.data?.message || 'Upload failed');
      }
    } catch (err) {
      console.error('Error uploading:', err);
      if (err instanceof SyntaxError) {
        toast.error('Invalid JSON file format');
      } else {
        toast.error(err.response?.data?.message || 'Upload failed');
      }
    } finally {
      setUploading(false);
    }
  };

  // Toggle calendar status
  const handleToggle = async (id, currentStatus) => {
    try {
      const res = await api.patch(`/admin/calendars/${id}/toggle`);
      if (res.data?.success) {
        toast.success(res.data.message);
        loadCalendars();
      }
    } catch (err) {
      console.error('Error toggling:', err);
      toast.error('Failed to toggle calendar status');
    }
  };

  // Delete calendar
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this calendar?')) {
      return;
    }

    try {
      const res = await api.delete(`/admin/calendars/${id}`);
      if (res.data?.success) {
        toast.success('Calendar deleted successfully');
        loadCalendars();
      }
    } catch (err) {
      console.error('Error deleting:', err);
      toast.error('Failed to delete calendar');
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="telugu-calendar-page">
      {/* Hero Section with Stats */}
      <div className="calendar-hero">
        <div className="hero-content">
          <div>
            <h1 className="calendar-title">
              <MdCalendarMonth /> Calendar Manager
            </h1>
            <p className="calendar-subtitle">Upload and manage Telugu calendars for Telangana & Andhra Pradesh</p>
          </div>
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-number">{stats.total}</div>
            <div className="stat-label">Total Calendars</div>
          </div>
          <div className="stat-card active">
            <div className="stat-number">{stats.active}</div>
            <div className="stat-label">Active</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.years.length}</div>
            <div className="stat-label">Years</div>
          </div>
        </div>
      </div>

      {/* Upload Section */}
      <div className="calendar-card upload-card">
        <div className="upload-header">
          <div>
            <h2 className="card-title">Add New Calendar</h2>
            <p className="card-subtitle">Upload a Telugu calendar JSON file</p>
          </div>
          <div className="upload-icon">
            <MdUploadFile size={32} />
          </div>
        </div>

        <div className="upload-form">
          <div className="form-row">
            <div className="form-group">
              <label>Select State</label>
              <select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                className="form-select form-select-icon"
              >
                <option value="Telangana">Telangana</option>
                <option value="Andhra Pradesh">Andhra Pradesh</option>
              </select>
            </div>

            <div className="form-group">
              <label>Choose JSON File</label>
              <div className="file-input-wrapper">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleFileSelect}
                  className="file-input"
                />
                <button className="file-button">
                  <span className="file-icon">
                    <MdUploadFile size={18} />
                  </span>
                  <span className="file-name">{selectedFile?.name || 'Select file'}</span>
                </button>
              </div>
            </div>
          </div>

          <p className="file-hint">💡 Format: telangana_calendar_august_2026.json (year & month extracted automatically)</p>

          <button
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            className="btn-upload btn-large"
          >
            {uploading ? (
              <>
                <span className="spinner-mini"></span>
                Importing...
              </>
            ) : (
              <>
                <MdUploadFile size={18} />
                Import Calendar
              </>
            )}
          </button>
        </div>
      </div>

      {/* Month Coverage Timeline */}
      {calendars.length > 0 && (
        <div className="calendar-card coverage-card">
          <h3 className="card-title">Month Coverage {filterState && `— ${filterState}`}</h3>
          <div className="month-timeline">
            {MONTH_NAMES.map((month, idx) => {
              const hasCal = monthCoverage[idx];
              return (
                <button
                  key={idx}
                  className={`month-badge ${hasCal ? 'active' : ''} ${filterMonth === (idx + 1) ? 'selected' : ''}`}
                  onClick={() => setFilterMonth(filterMonth === (idx + 1) ? '' : (idx + 1))}
                  title={hasCal ? `${month} — ${MONTH_NAMES_TE[idx]}` : `No calendar for ${month}`}
                >
                  <span className="month-num">{month}</span>
                  {hasCal && <span className="coverage-dot"></span>}
                </button>
              );
            })}
          </div>
          <p className="coverage-hint">Click a month to filter. Green indicates available calendars.</p>
        </div>
      )}

      {/* Filter Bar */}
      <div className="filter-bar">
        <div className="filter-controls">
          <select
            value={filterState}
            onChange={(e) => {
              setFilterState(e.target.value);
              setFilterMonth('');
            }}
            className="filter-select"
          >
            <option value="">All States</option>
            <option value="Telangana">Telangana</option>
            <option value="Andhra Pradesh">Andhra Pradesh</option>
          </select>

          <input
            type="text"
            placeholder="Filter by year (e.g., 2026)"
            value={filterYear}
            onChange={(e) => setFilterYear(e.target.value)}
            className="filter-input"
          />

          <button onClick={loadCalendars} className="btn-filter-refresh">
            <MdRefresh size={16} />
            Refresh
          </button>

          {(filterState || filterYear || filterMonth) && (
            <button
              onClick={() => {
                setFilterState('');
                setFilterYear('');
                setFilterMonth('');
              }}
              className="btn-filter-clear"
            >
              Clear Filters
            </button>
          )}
        </div>
        <div className="filter-count">
          Showing {filteredCalendars.length} of {calendars.length}
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="calendars-section">
        <h3 className="section-title">Imported Calendars</h3>

        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading calendars...</p>
          </div>
        ) : filteredCalendars.length === 0 ? (
          <div className="empty-state">
            <MdCalendarMonth size={48} />
            <p>{calendars.length === 0 ? 'No calendars yet. Upload one to get started!' : 'No calendars match your filters.'}</p>
          </div>
        ) : (
          <div className="calendar-grid">
            {filteredCalendars.map((cal) => (
              <div key={cal.id} className={`calendar-item ${!cal.is_active ? 'inactive' : ''}`}>
                <button
                  className="calendar-item-clickable"
                  onClick={() => loadCalendarDetail(cal)}
                  title="View details"
                  disabled={loadingDetail}
                >
                  <div className="calendar-item-header">
                    <div className="month-info">
                      <div className="month-display">
                        <span className="month-en">{MONTH_NAMES[cal.month - 1]}</span>
                        <span className="month-te">{cal.month_name_te || MONTH_NAMES_TE[cal.month - 1]}</span>
                      </div>
                      <div className="year-display">{cal.year}</div>
                    </div>
                    <span className={`status-badge ${cal.is_active ? 'status-active' : 'status-inactive'}`}>
                      {cal.is_active ? (
                        <>
                          <MdCheckCircle size={14} /> Active
                        </>
                      ) : (
                        <>
                          <MdPending size={14} /> Inactive
                        </>
                      )}
                    </span>
                  </div>

                  <div className="calendar-item-body">
                    <div className="item-row">
                      <span className="item-label">State</span>
                      <span className="item-value">{cal.state}</span>
                    </div>
                    <div className="item-row">
                      <span className="item-label">Uploaded</span>
                      <span className="item-value">{formatDate(cal.created_at)}</span>
                    </div>
                  </div>
                </button>

                <div className="calendar-item-footer">
                  <button
                    onClick={() => handleToggle(cal.id, cal.is_active)}
                    className={`btn-item ${cal.is_active ? 'btn-deactivate' : 'btn-activate'}`}
                    title={cal.is_active ? 'Deactivate calendar' : 'Activate calendar'}
                  >
                    {cal.is_active ? 'Deactivate' : 'Activate'}
                  </button>

                  <button
                    onClick={() => handleDelete(cal.id)}
                    className="btn-item btn-delete"
                    title="Delete calendar"
                  >
                    <MdDelete size={16} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Info */}
      {calendars.length > 0 && (
        <div className="calendar-card info-card">
          <div className="info-header">
            <h4>📚 Quick Reference</h4>
          </div>
          <div className="info-content">
            <p><strong>Mobile API:</strong> <code>GET /api/calendars?year=2026&month=8&state=Telangana</code></p>
            <p><strong>Status:</strong> Only <strong>Active</strong> calendars are served to mobile apps. Use <strong>Inactive</strong> for testing.</p>
            <p><strong>Updates:</strong> To update a calendar, delete the old one and upload the new file.</p>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {detailCalendar && (
        <>
          <div className="modal-overlay" onClick={() => setDetailCalendar(null)} />
          <div className="modal-content detail-modal">
            <div className="modal-header">
              <div>
                <h2 className="modal-title">
                  {MONTH_NAMES[detailCalendar.month - 1]} {detailCalendar.year}
                </h2>
                <p className="modal-subtitle">{detailCalendar.state}</p>
              </div>
              <button
                className="modal-close"
                onClick={() => setDetailCalendar(null)}
                title="Close"
                disabled={loadingDetail}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              {loadingDetail ? (
                <div className="loading-state" style={{ marginTop: '40px' }}>
                  <div className="spinner"></div>
                  <p>Loading calendar days...</p>
                </div>
              ) : (
                <>
              <div className="detail-section">
                <h3>Calendar Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Month (English)</label>
                    <div className="detail-value">{MONTH_NAMES[detailCalendar.month - 1]}</div>
                  </div>
                  <div className="detail-item">
                    <label>Month (Telugu)</label>
                    <div className="detail-value">{detailCalendar.month_name_te || MONTH_NAMES_TE[detailCalendar.month - 1]}</div>
                  </div>
                  <div className="detail-item">
                    <label>Year</label>
                    <div className="detail-value">{detailCalendar.year}</div>
                  </div>
                  <div className="detail-item">
                    <label>State</label>
                    <div className="detail-value">{detailCalendar.state}</div>
                  </div>
                  <div className="detail-item">
                    <label>Status</label>
                    <div className={`status-badge ${detailCalendar.is_active ? 'status-active' : 'status-inactive'}`}>
                      {detailCalendar.is_active ? (
                        <>
                          <MdCheckCircle size={14} /> Active
                        </>
                      ) : (
                        <>
                          <MdPending size={14} /> Inactive
                        </>
                      )}
                    </div>
                  </div>
                  <div className="detail-item">
                    <label>Uploaded</label>
                    <div className="detail-value">{formatDate(detailCalendar.created_at)}</div>
                  </div>
                </div>
              </div>

              {detailCalendar.calendar_data && (
                <div className="detail-section">
                  <h3>Calendar Days ({(() => {
                    try {
                      const calData = typeof detailCalendar.calendar_data === 'string'
                        ? JSON.parse(detailCalendar.calendar_data)
                        : detailCalendar.calendar_data;
                      return (calData.days || []).length;
                    } catch {
                      return '?';
                    }
                  })()})</h3>
                  {(() => {
                    try {
                      const calData = typeof detailCalendar.calendar_data === 'string'
                        ? JSON.parse(detailCalendar.calendar_data)
                        : detailCalendar.calendar_data;

                      const days = calData.days || [];

                      return days.length > 0 ? (
                        <div className="days-list">
                          {days.map((day, idx) => (
                            <div key={idx} className={`day-item ${day.holiday ? 'is-holiday' : ''}`}>
                              <div className="day-date">
                                {day.date}
                              </div>
                              <div className="day-details">
                                <div className="detail-row">
                                  <span className="day-name-en">{day.day}</span>
                                  <span className="day-name-te">{day.day_te}</span>
                                  {day.is_sunday && <span className="sunday-badge">Sunday</span>}
                                </div>

                                {day.display_lines_te && Array.isArray(day.display_lines_te) ? (
                                  day.display_lines_te.map((line, idx) => (
                                    <div key={idx} className="detail-row panchang-line">
                                      <span className="value-te">{line}</span>
                                    </div>
                                  ))
                                ) : (
                                  <>
                                    {Array.isArray(day.tithi) && day.tithi.length > 0 && (
                                      <div className="detail-row">
                                        <span className="label">Tithi:</span>
                                        <span className="value-te">{day.tithi[0]?.display_text || day.tithi[0]?.name_te}</span>
                                      </div>
                                    )}
                                    {Array.isArray(day.nakshatra) && day.nakshatra.length > 0 && (
                                      <div className="detail-row">
                                        <span className="label">Nakshatra:</span>
                                        <span className="value-te">{day.nakshatra[0]?.display_text || day.nakshatra[0]?.name_te}</span>
                                      </div>
                                    )}
                                    {Array.isArray(day.varjyam) && day.varjyam.length > 0 && (
                                      <div className="detail-row">
                                        <span className="label">Varjyam:</span>
                                        <span className="value-te small">{day.varjyam[0]?.display_text}</span>
                                      </div>
                                    )}
                                  </>
                                )}

                                {Array.isArray(day.events) && day.events.length > 0 && (
                                  <div className="events-section">
                                    {day.events.map((event, idx) => (
                                      <div key={idx} className="event-item">🌟 {event}</div>
                                    ))}
                                  </div>
                                )}

                                {day.holiday && (
                                  <div className="detail-row">
                                    <span className="holiday-label">
                                      🎉 {day.holiday?.name_te || day.holiday?.name_en}
                                      {day.holiday?.moon_sighting_dependent && <span className="moon-note"> (Moon Sighting)</span>}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="no-data">No calendar days found in this calendar</p>
                      );
                    } catch (err) {
                      return (
                        <div className="detail-code">
                          <pre>{JSON.stringify(typeof detailCalendar.calendar_data === 'string' ? JSON.parse(detailCalendar.calendar_data) : detailCalendar.calendar_data, null, 2)}</pre>
                        </div>
                      );
                    }
                  })()}
                </div>
              )}
                </>
              )}
            </div>

            <div className="modal-footer">
              <button
                onClick={() => handleToggle(detailCalendar.id, detailCalendar.is_active)}
                className={`btn-modal ${detailCalendar.is_active ? 'btn-deactivate' : 'btn-activate'}`}
              >
                {detailCalendar.is_active ? 'Deactivate' : 'Activate'}
              </button>

              <button
                onClick={() => {
                  handleDelete(detailCalendar.id);
                  setDetailCalendar(null);
                }}
                className="btn-modal btn-delete"
              >
                Delete
              </button>

              <button
                onClick={() => setDetailCalendar(null)}
                className="btn-modal btn-close"
              >
                Close
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
